const { createApp, ref, watch, onMounted } = Vue;

createApp({
  setup() {
    let content = ref({
      orderTitle: "Estado de tu pedido #",
    });
    let activeTab = ref("0");
    let loading = ref(true);
    let order = ref(null);
    let emptyOrder = ref(false);
    let error = ref(false);
    let checking = ref(false);
    let currentStep = ref(0);
    let products = ref([]);
    let stepDates = ref([]);
    let hasComments = ref(false);
    let orderNumber = ref("");
    let invoiceNumber = ref("");
    let createdDate = ref("--/--/--");
    let createdTime = ref("--:--");
    let estimatedDelivery = ref("--/--/--");

    /**
     * Changes the active tab.
     *
     * @param {string} tab - The new active tab.
     * @return {void}
     */
    const changeTab = (tab) => {
      activeTab.value = tab;
      error.value = false;
    };

    /**
     * Tracks an order by sending a POST request to the provided API endpoint.
     *
     * @param {string} orderNumber - The short order number to track.
     * @return {void}
     */
    const trackOrder = async (e) => {
      e.preventDefault();
      console.log("trackOrder", e.target);
      checking.value = true;

      const myHeaders = new Headers();

      myHeaders.append(
        "Authorization",
        serviceAuthorization.trackOrder
      );

      const formdata = new FormData(e.target);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          servicesApp.trackOrder,
          requestOptions
        );

        const result = await response.json();

        console.log("result", result);

        if (Array.isArray(result) && result.length === 0) {
          error.value = true;
          checking.value = false;

          invoiceNumber.value = "";
          orderNumber.value = "";

          return;
        }

        order.value = result;

        content.value = {
          ...content.value,
          orderTitle: `Estado de tu pedido #${order.value.short_order_number}`,
        };

        checking.value = true;

        eightDaysAfter(order.value.estimated_delivery);

        if (!order.value.tracking) {
          emptyOrder.value = true;
          dayZero(order.value.created);
          return;
        }

        currentStep.value = +order.value.tracking.despacho.estado;

        getProducts(order.value.tracking.producto);
        getStepDates({
          tracking: order.value.tracking.historicoDespacho,
          status: order.value.tracking.despacho.estado,
        });
        setComment({
          transporteId: order.value.tracking.viaje.transporteId,
          estado: order.value.tracking.despacho.estado,
        });
      } catch (error) {
        console.error("error", error);
        error.value = true;

        checking.value = true;
      }
    };

    /**
     * Sets the `hasComments` property depending on the given `transporteId` and `estado`.
     *
     * @param {object} options - An object with the following properties:
     *   - {number} transporteId - The `transporteId` to check.
     *   - {string} estado - The `estado` to check.
     * @return {void}
     */
    const setComment = ({ transporteId, estado }) => {
      if (estado === "6") {
        hasComments.value = true;
      } else {
        hasComments.value = false;
      }
    };

    /**
     * Formats a given value as money in Danish (da-DK) locale.
     *
     * @param {number} value - The value to be formatted as money.
     * @return {string} The formatted value as a string with the currency symbol.
     */
    const formatMoney = (value) => {
      const formattedValue = new Intl.NumberFormat("da-DK", {
        style: "decimal",
      }).format(Math.floor(value));

      if (formattedValue === "NaN") return "0.00";

      return `$${formattedValue}`;
    };

    /**
     * Retrieves a list of products based on the provided order SKUs.
     *
     * @param {Array} orderSkus - An array of objects containing order SKUs.
     * @return {undefined} No return value, but updates the products.value with the retrieved products.
     */
    const getProducts = async (orderSkus) => {
      if (!orderSkus) return;

      const skuList = orderSkus.map((sku) => sku.codigoEan);

      const response = await fetch(`/products.json?limit=500`);
      const result = await response.json();

      const variants = result.products.map((product) => {
        return product.variants;
      });

      const productsList = [];

      variants.flat().forEach((variant) => {
        if (skuList.includes(variant.sku)) {
          const variantProduct = result.products.find(
            (product) => product.id === variant.product_id
          );

          variant.product_variant = variantProduct;
          variant.price_with_currency = formatMoney(variant.price);

          const sku = orderSkus.find((sku) => sku.codigoEan === variant.sku);

          if (sku) {
            variant.quantity = `x${sku.venta.cantidad}`;

            variant.total_with_currency = formatMoney(
              variant.price * sku.venta.cantidad
            );
          }

          productsList.push(variant);
        }
      });

      if (productsList.length > 0) {
        products.value = productsList;
      }
    };

    /**
     * Formats a date string in the format "YYYY-MM-DD" to "DD/MM/YYYY".
     *
     * @param {string} date - The date string in the format "YYYY-MM-DD".
     * @return {string} The formatted date string in the format "DD/MM/YYYY".
     */
    const formatStepDate = (date) => {
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    };

    /**
     * Formats a given time string into a 12-hour format with AM/PM.
     *
     * @param {string} time - The time string to be formatted, in the format "HH:MM:SS".
     * @return {string} The formatted time string in the format "HH:MM AM/PM".
     */
    const formatTime = (time) => {
      const [hours, minutes, seconds] = time.split(":");
      const hour = Number(hours);
      const ampm = hour < 12 ? "AM" : "PM";
      const formattedHour = hour <= 12 ? hour : hour - 12;

      return `${formattedHour < 10 ? "0" + formattedHour : formattedHour
        }:${minutes} ${ampm}`;
    };

    /**
     * Extracts and formats date information from a given tracking object.
     *
     * @param {Array} tracking - An array of objects containing fechaCreacion property.
     * @return {void}
     */
    const getStepDates = ({ tracking, status }) => {
      if (!tracking || !status) return;

      let steps = Array(+status).fill({
        status: false,
        date: "",
        time: "",
      });

      tracking.forEach((step) => {
        const date_time = step.fechaCreacion.split(" ");
        steps[step.codigoEstado - 1] = {
          status: true,
          date: formatStepDate(date_time[0]),
          time: formatTime(date_time[1]),
        };
      });

      const createdDate = formatISODate(order.value.created);

      steps = [
        {
          status: true,
          date: createdDate.date,
          time: createdDate.time + " " + createdDate.hour12,
        },
        ...steps,
      ];

      console.log("steps", steps);

      stepDates.value = steps;
    };

    /**
     * Validates whether the input value is a number.
     *
     * @param {Event} e - The event object triggered by the input change.
     * @return {void}
     */
    const validateOrderNumber = (e) => {
      const input = e.target;
      const value = +input.value;
      const regex = /^[0-9]+$/;

      if (!regex.test(value)) {
        input.setCustomValidity("Solo se admiten numeros!");
      } else {
        input.setCustomValidity("");
      }
    };

    /**
     * Validates the invoice number input to ensure it contains both letters and numbers.
     *
     * @param {object} e - The event object triggered by the input change.
     * @return {void}
     */
    const validateInvoiceNumber = (e) => {
      const input = e.target;
      const value = input.value;
      const regex = /^(?=.*[a-zA-Z])[a-zA-Z0-9]+$/;

      if (!regex.test(value)) {
        input.setCustomValidity("La factura debe contener letras y numeros!");
      } else {
        input.setCustomValidity("");
      }
    };

    /**
     * Initializes the created date to the given initial date.
     *
     * @param {Date} initialDate - The initial date to set as the created date.
     * @return {void}
     */
    const dayZero = (initialDate) => {
      const date = formatISODate(initialDate);

      createdDate.value = date.date;

      createdTime.value = date.time + " " + date.hour12;
    };

    /**
     * Calculates the estimated delivery date by adding 8 days to the initial date.
     *
     * @param {Date} initialDate - The initial date to calculate the estimated delivery date from.
     * @return {void}
     */
    const eightDaysAfter = (initialDate) => {
      const date = new Date(initialDate);

      date.setDate(date.getDate() + 8);
      estimatedDelivery.value =
        ("0" + date.getDate()).slice(-2) +
        "/" +
        ("0" + (date.getMonth() + 1)).slice(-2) +
        "/" +
        date.getFullYear();
    };

    /**
     * Receives a date in ISO format and returns an object with the date and time formatted.
     *
     * @param {string} dateISO - The date in ISO format.
     * @return {{date: string, time: string}}
     */
    const formatISODate = (dateISO) => {
      const date = new Date(dateISO);

      let dateFormatted = new Intl.DateTimeFormat("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(date);

      let timeFormatted = new Intl.DateTimeFormat("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(date);

      timeFormatted = timeFormatted.split(" ");

      return {
        date: dateFormatted,
        time: timeFormatted[0],
        hour12: timeFormatted[1].includes("p") ? "PM" : "AM",
      };
    };

    onMounted(() => {
      loading.value = false;
    });

    return {
      loading,
      order,
      emptyOrder,
      error,
      checking,
      content,
      activeTab,
      changeTab,
      trackOrder,
      currentStep,
      products,
      stepDates,
      hasComments,
      orderNumber,
      invoiceNumber,
      validateOrderNumber,
      validateInvoiceNumber,
      estimatedDelivery,
      createdDate,
      createdTime,
    };
  },
}).mount("#check_order");
